import React, { useEffect, useMemo, useRef, useState } from 'react';
import { displayCells } from '../../game';
import { computeTileSize } from '../../game/presentation';

interface PuzzleBoardProps {
  phrase: string;
  category: string;
  guessedLetters: string[];
  reducedMotion?: boolean;
}

const PuzzleBoard: React.FC<PuzzleBoardProps> = ({
  phrase,
  category,
  guessedLetters,
  reducedMotion = false,
}) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const boardRef = useRef<HTMLElement>(null);
  const [width, setWidth] = useState(340);
  const [heightForTiles, setHeightForTiles] = useState<number | undefined>(undefined);
  const prevRevealed = useRef<Set<number>>(new Set());

  useEffect(() => {
    prevRevealed.current = new Set();
  }, [phrase]);

  useEffect(() => {
    const words = wrapRef.current;
    const board = boardRef.current;
    if (!words) return undefined;
    const media = window.matchMedia('(max-width: 639px)');
    const measure = () => {
      setWidth(Math.floor(words.clientWidth));
      if (board && media.matches) {
        setHeightForTiles(Math.floor(board.clientHeight));
      } else {
        setHeightForTiles(undefined);
      }
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(words);
    if (board) observer.observe(board);
    media.addEventListener('change', measure);
    return () => {
      observer.disconnect();
      media.removeEventListener('change', measure);
    };
  }, []);

  const tile = computeTileSize(phrase, Math.max(160, width - 8), heightForTiles);
  const cells = useMemo(() => displayCells(phrase, guessedLetters), [phrase, guessedLetters]);

  const wordGroups = useMemo(() => {
    const groups: Array<{ start: number; cells: typeof cells }> = [];
    let current: typeof cells = [];
    let start = 0;
    cells.forEach((cell, index) => {
      if (cell.isSpace) {
        if (current.length) {
          groups.push({ start, cells: current });
          current = [];
        }
        return;
      }
      if (current.length === 0) {
        start = index;
      }
      current.push(cell);
    });
    if (current.length) {
      groups.push({ start, cells: current });
    }
    return groups;
  }, [cells]);

  const revealedIndexes = useMemo(() => {
    const next = new Set<number>();
    cells.forEach((cell, index) => {
      if (cell.revealed && !cell.isSpace) {
        next.add(index);
      }
    });
    return next;
  }, [cells]);

  const newborn = useMemo(() => {
    const fresh = new Set<number>();
    revealedIndexes.forEach((index) => {
      if (!prevRevealed.current.has(index)) {
        fresh.add(index);
      }
    });
    return fresh;
  }, [revealedIndexes]);

  useEffect(() => {
    prevRevealed.current = revealedIndexes;
  }, [revealedIndexes]);

  return (
    <section ref={boardRef} className="ss-puzzle" aria-label={`Puzzle category ${category || 'unknown'}`}>
      <span className="ss-category">{category || 'Puzzle'}</span>
      <div ref={wrapRef} className="ss-words">
        {wordGroups.map((group) => (
          <div className="ss-word" key={`w-${group.start}`}>
            {group.cells.map((cell, offset) => {
              const index = group.start + offset;
              const shown = cell.revealed;
              const isNew = shown && newborn.has(index) && !reducedMotion;
              return (
                <span
                  key={index}
                  className={`ss-tile${shown ? ' is-shown' : ''}${cell.isPunctuation ? ' is-punct' : ''}${
                    isNew ? ' is-new' : ''
                  }`}
                  style={{ width: tile, height: Math.round(tile * 1.25), fontSize: Math.max(14, tile * 0.62) }}
                  aria-hidden={!shown}
                >
                  {shown ? cell.char.toUpperCase() : ''}
                </span>
              );
            })}
          </div>
        ))}
      </div>
    </section>
  );
};

export default PuzzleBoard;
